import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { RoomDetailDto } from '../types/community'
import { getRoom, postPresence } from '../lib/api/rooms'
import { CotApiError } from '../lib/api/client'

/** Full room detail (member-only; the query errors 404 for non-members). */
export function useRoom(id: string | undefined, actingPersonId?: string) {
  return useQuery<RoomDetailDto>({
    queryKey: ['community', 'rooms', 'detail', id, actingPersonId ?? null],
    queryFn: () => getRoom(id as string),
    enabled: !!id,
    retry: false,
    refetchInterval: (query) => {
      const status = query.state.error instanceof CotApiError ? query.state.error.status : undefined
      return query.state.data?.info.isOpen === false || (status && [403, 404, 410].includes(status)) ? false : 5_000
    },
  })
}

/**
 * Presence heartbeat while the room screen is mounted (COTC_Rooms R2): posts a "lobby"
 * heartbeat immediately and every 30s (recursive setTimeout), and a final "left" on unmount
 * (roomLeave). Heartbeat failures are swallowed — they must never surface to the child.
 */
export function useRoomPresence(id: string | undefined, actingPersonId: string | undefined, enabled: boolean) {
  useEffect(() => {
    if (!enabled || !id) return

    let stopped = false
    let handle: ReturnType<typeof setTimeout>
    const beat = () => {
      void postPresence(id, { state: 'lobby', expectedPersonId: actingPersonId }).catch(() => {})
    }
    const loop = () => {
      handle = setTimeout(() => {
        if (stopped) return
        beat()
        loop()
      }, 30_000)
    }

    beat() // immediate heartbeat on mount
    loop()

    return () => {
      stopped = true
      clearTimeout(handle)
      // The guard prevents an old screen's cleanup from deleting a newly switched profile.
      void postPresence(id, { state: 'left', expectedPersonId: actingPersonId }).catch(() => {})
    }
  }, [enabled, id, actingPersonId])
}
